import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import type {
  AdminCashbackLedgerResponse,
  CashbackSplitPublic,
  ProviderPublic,
} from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumbs } from "@/components/breadcrumbs";

function fmtMoney(cents: number, currency: string): string {
  return `${currency === "ZAR" ? "R " : `${currency} `}${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export default function AdminCashbackPage() {
  const { id: providerId } = useParams<{ id: string }>();
  const [provider, setProvider] = useState<ProviderPublic | null>(null);
  const [split, setSplit] = useState<CashbackSplitPublic | null>(null);
  const [ledger, setLedger] = useState<AdminCashbackLedgerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!providerId) return;
    setError(null);
    try {
      const [p, s, l] = await Promise.all([
        api.getShopProvider(providerId).catch(() => null), // endpoint accepts ID or slug
        api.getCashbackSplit(providerId).catch(() => ({ split: null })),
        api.getAdminCashbackLedger({ provider_id: providerId, page_size: 100 }),
      ]);
      if (p) setProvider(p.provider);
      setSplit(s?.split ?? null);
      setLedger(l);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load cashback data");
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => { void refresh(); }, [refresh]);

  if (loading) return <p className="text-muted-foreground" data-page="admin-cashback" data-state="loading">Loading…</p>;
  if (error && !ledger) {
    return (
      <div data-page="admin-cashback" data-state="error" className="flex flex-col gap-3">
        <h1 className="text-2xl font-semibold">Cashback</h1>
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div data-page="admin-cashback" data-state="ready" className="flex flex-col gap-6">
      <Breadcrumbs items={[
        { label: "Admin" },
        { label: "Shop providers", to: "/admin/shop/providers" },
        { label: provider?.name ?? "Provider", to: providerId ? `/admin/shop/providers/${providerId}/edit` : "#" },
        { label: "Cashback" },
      ]} />
      <h1 className="text-2xl font-semibold">{provider?.name ?? "Provider"} -- Cashback</h1>
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      <SplitEditor providerId={providerId!} split={split} onSaved={refresh} />
      <BatchIngest providerId={providerId!} onIngested={refresh} />
      <LedgerTable ledger={ledger} onReversed={refresh} />
    </div>
  );
}

function SplitEditor({
  providerId,
  split,
  onSaved,
}: { providerId: string; split: CashbackSplitPublic | null; onSaved: () => void | Promise<void> }) {
  const [parent, setParent] = useState(split?.parent_pct ?? 50);
  const [school, setSchool] = useState(split?.school_pct ?? 30);
  const [dugads, setDugads] = useState(split?.dugads_pct ?? 20);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (split) {
      setParent(split.parent_pct);
      setSchool(split.school_pct);
      setDugads(split.dugads_pct);
    }
  }, [split]);

  const sum = parent + school + dugads;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (sum !== 100) {
      setMsg("Percentages must sum to 100");
      return;
    }
    setSaving(true);
    try {
      await api.updateCashbackSplit(providerId, { parent_pct: parent, school_pct: school, dugads_pct: dugads });
      setMsg("Split saved. Applies to future accruals only.");
      await onSaved();
    } catch (e) {
      setMsg(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card data-region="split-editor">
      <CardHeader><CardTitle>Split</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="parent_pct">Parent %</Label>
            <Input id="parent_pct" type="number" min={0} max={100} value={parent} onChange={(e) => setParent(Number(e.target.value) || 0)} className="w-24" />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="school_pct">School %</Label>
            <Input id="school_pct" type="number" min={0} max={100} value={school} onChange={(e) => setSchool(Number(e.target.value) || 0)} className="w-24" />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="dugads_pct">Dugads %</Label>
            <Input id="dugads_pct" type="number" min={0} max={100} value={dugads} onChange={(e) => setDugads(Number(e.target.value) || 0)} className="w-24" />
          </div>
          <div className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Sum</span>
            <span className={sum === 100 ? "font-semibold text-emerald-600" : "font-semibold text-destructive"} data-field="sum">
              {sum}%
            </span>
          </div>
          <Button type="submit" disabled={saving || sum !== 100} data-action="save-split">{saving ? "Saving…" : "Save split"}</Button>
        </form>
        {msg && <p className="mt-3 text-sm text-muted-foreground" data-status>{msg}</p>}
      </CardContent>
    </Card>
  );
}

function BatchIngest({
  providerId,
  onIngested,
}: { providerId: string; onIngested: () => void | Promise<void> }) {
  const [json, setJson] = useState(`[\n  { "external_account_ref": "EF-1234", "external_event_ref": "TXN-001", "spend_cents": 10000, "reported_at": "${new Date().toISOString()}" }\n]`);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    let parsed: unknown;
    try { parsed = JSON.parse(json); }
    catch { setError("Payload must be valid JSON"); return; }
    if (!Array.isArray(parsed)) { setError("Payload must be a JSON array of events"); return; }
    setSubmitting(true);
    try {
      const r = await api.ingestCashbackBatch(providerId, {
        source: "manual_json",
        events: parsed as Parameters<typeof api.ingestCashbackBatch>[1]["events"],
      });
      const skippedSummary = r.skipped_reasons.length > 0
        ? ` Skipped: ${r.skipped_reasons.slice(0, 3).map((s) => `${s.external_event_ref} (${s.reason})`).join("; ")}${r.skipped_reasons.length > 3 ? "…" : ""}`
        : "";
      setResult(`Accrued ${r.accrued_count}, skipped ${r.skipped_count}.${skippedSummary}`);
      await onIngested();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Ingest failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card data-region="batch-ingest">
      <CardHeader><CardTitle>Ingest spend batch</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <Label htmlFor="payload">JSON array of events</Label>
          <textarea
            id="payload"
            rows={10}
            value={json}
            onChange={(e) => setJson(e.target.value)}
            className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
            data-field="payload"
            title="JSON array of spend events"
            aria-label="JSON array of spend events"
          />
          <p className="text-xs text-muted-foreground">
            Each event needs <code>external_account_ref</code>, <code>external_event_ref</code> (unique per provider -- retries are skipped), <code>spend_cents</code>, and <code>reported_at</code> (ISO).
          </p>
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}
          {result && <p className="text-sm text-emerald-700" data-status>{result}</p>}
          <div>
            <Button type="submit" disabled={submitting} data-action="ingest">{submitting ? "Ingesting…" : "Ingest"}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function LedgerTable({
  ledger,
  onReversed,
}: { ledger: AdminCashbackLedgerResponse | null; onReversed: () => void | Promise<void> }) {
  if (!ledger) return null;

  async function handleReverse(accrualId: string) {
    const reason = prompt("Reason for reversal?");
    if (!reason) return;
    try {
      await api.reverseCashbackAccrual(accrualId, reason);
      await onReversed();
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Reversal failed");
    }
  }

  return (
    <Card data-region="ledger">
      <CardHeader>
        <CardTitle>Ledger ({ledger.total} accruals)</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid gap-2 rounded-md border bg-muted/30 p-3 text-sm sm:grid-cols-4" data-region="totals">
          <div><span className="text-muted-foreground">Total spend:</span> <span className="font-semibold">{fmtMoney(ledger.totals.spend_cents, "ZAR")}</span></div>
          <div><span className="text-muted-foreground">Parent:</span> <span className="font-semibold">{fmtMoney(ledger.totals.parent_cents, "ZAR")}</span></div>
          <div><span className="text-muted-foreground">School:</span> <span className="font-semibold">{fmtMoney(ledger.totals.school_cents, "ZAR")}</span></div>
          <div><span className="text-muted-foreground">Dugads:</span> <span className="font-semibold">{fmtMoney(ledger.totals.dugads_cents, "ZAR")}</span></div>
        </div>

        {ledger.accruals.length === 0 ? (
          <p className="text-sm text-muted-foreground">No accruals yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="pb-2">Reported</th>
                  <th className="pb-2">School</th>
                  <th className="pb-2 text-right">Spend</th>
                  <th className="pb-2 text-right">P / S / D</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {ledger.accruals.map((a) => (
                  <tr key={a.id} data-row={a.id}>
                    <td className="py-2 text-xs">{fmtDate(a.reported_at)}</td>
                    <td className="py-2">{a.school_name ?? <span className="text-muted-foreground">Unattributed</span>}</td>
                    <td className="py-2 text-right">{fmtMoney(a.spend_cents, a.currency)}</td>
                    <td className="py-2 text-right text-xs">
                      {fmtMoney(a.parent_cents, a.currency)} / {fmtMoney(a.school_cents, a.currency)} / {fmtMoney(a.dugads_cents, a.currency)}
                    </td>
                    <td className="py-2"><span data-status={a.status} className="rounded-full bg-muted px-2 py-0.5 text-xs">{a.status}</span></td>
                    <td className="py-2 text-right">
                      {a.status === "accrued" && (
                        <button
                          type="button"
                          onClick={() => handleReverse(a.id)}
                          data-action="reverse"
                          className="text-xs text-destructive hover:underline"
                        >
                          Reverse
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
